import { getRegisteredStore } from "app/storeRegistry";
import { selectProcoreCompanyId, selectProcoreProjectId } from "slices/explorer";
import { log } from "utils/log";

import { getProcoreApiBaseUrl } from "../auth";
import { procoreClient } from "../client";

const l = log.analytics;

export type AnalyticEventPayload = {
    key: string;
    properties?: Record<string, unknown>;
    timestamp?: string;
};

/**
 * Track an analytics event to Procore's analytics system
 *
 * Automatically includes companyId, projectId, timestamp, library_name, and library_version
 * on every event. Silently no-ops if companyId or projectId are not configured.
 * No-ops in development mode to avoid polluting Amplitude with local data.
 *
 * @param key - The namespaced event key (use Events from 'apis/procore')
 * @param properties - Optional event-specific properties
 *
 * @example
 * ```ts
 * import { track, Events } from 'apis/procore';
 *
 * track(Events.toolbar.homeTapped);
 *
 * track(Events.objectTree.searched, {
 *   search_term: 'wall',
 *   result_count: 42,
 * });
 * ```
 */
export async function track(key: string, properties?: Record<string, unknown>): Promise<void> {
    const store = getRegisteredStore();

    if (!store) {
        l.error("analytics: event dropped, store not registered", { key });
        return;
    }

    const state = store.getState();
    const companyId = selectProcoreCompanyId(state);
    const projectId = selectProcoreProjectId(state);

    if (!companyId || !projectId) {
        return;
    }

    const payload: AnalyticEventPayload = {
        key,
        properties: {
            ...properties,
            library_name: __LIB_NAME__,
            library_version: __LIB_VERSION__,
        },
        timestamp: new Date().toISOString(),
    };

    l.info(`track ${key}`, payload.properties);

    if (import.meta.env.DEV) {
        return;
    }

    try {
        await procoreClient("/rest/v1.0/analytic_events", {
            method: "POST",
            params: {
                company_id: companyId,
                project_id: projectId,
            },
            body: payload,
        });
    } catch {
        // Silent fail for analytics - errors are not critical
    }
}

/**
 * Fire-and-forget analytics event safe for use during page unload.
 *
 * Uses `navigator.sendBeacon` which browsers guarantee to queue even
 * during `beforeunload` / `visibilitychange`, unlike async `fetch`
 * which is typically cancelled when the page is torn down.
 *
 * Falls back to a keepalive `fetch` when `sendBeacon` is unavailable,
 * and silently no-ops if neither mechanism is available.
 *
 * @param key - The namespaced event key (use Events from 'apis/procore')
 * @param properties - Optional event-specific properties
 */
export function trackBeacon(key: string, properties?: Record<string, unknown>): void {
    const store = getRegisteredStore();

    if (!store) {
        return;
    }

    const state = store.getState();
    const companyId = selectProcoreCompanyId(state);
    const projectId = selectProcoreProjectId(state);

    if (!companyId || !projectId) {
        return;
    }

    const payload: AnalyticEventPayload = {
        key,
        properties: {
            ...properties,
            library_name: __LIB_NAME__,
            library_version: __LIB_VERSION__,
        },
        timestamp: new Date().toISOString(),
    };

    l.info(`trackBeacon ${key}`, payload.properties);

    if (import.meta.env.DEV) {
        return;
    }

    const baseUrl = getProcoreApiBaseUrl();
    const url = `${baseUrl}/rest/v1.0/analytic_events` + `?company_id=${companyId}` + `&project_id=${projectId}`;

    const hasNavigator =
        typeof globalThis.navigator === "object" && typeof globalThis.navigator?.sendBeacon === "function";
    const hasFetch = typeof globalThis.fetch === "function";
    const hasBlob = typeof globalThis.Blob === "function";

    // If neither sendBeacon nor fetch is available, silently no-op.
    if (!hasNavigator && !hasFetch) {
        return;
    }

    // If we cannot construct a Blob for the payload, silently no-op to avoid runtime errors.
    if (!hasBlob) {
        return;
    }

    const blob = new globalThis.Blob([JSON.stringify(payload)], { type: "application/json" });

    // sendBeacon is the preferred mechanism — it's fire-and-forget and
    // guaranteed to be queued by the browser even during page unload.
    if (hasNavigator && globalThis.navigator.sendBeacon(url, blob)) {
        return;
    }

    // Fallback: keepalive fetch (also survives page unload in most browsers)
    if (!hasFetch) {
        // No available transport mechanism; silently no-op.
        return;
    }

    globalThis
        .fetch(url, {
            method: "POST",
            body: blob,
            keepalive: true,
            credentials: "include",
        })
        .catch(() => {
            // Silent fail — analytics are best-effort
        });
}
